export const SAI_ORIGIN='https://sai.dope-ai.kr';
export type EmbedLease={token:string;grant:string;workspace:string;principalId:string;expiresAt:number;policyVersion:number;memberVersion:number};
type Options={workspace:string;parent:unknown;post:(value:unknown)=>void;exchange:(grant:string)=>Promise<{token:string;binding?:{tenantId:string;principalId:string}}>;retire:()=>void;now:()=>number;nonce:()=>string};

export function createEmbedAccessController(options:Options){
 let lease:EmbedLease|null=null,sequence=0,challenge='',stopped=false;
 let timer:ReturnType<typeof setTimeout>|undefined;
 const subscribers=new Set<()=>void>();
 const emit=()=>subscribers.forEach(fn=>fn());
 const revoke=()=>{sequence++;lease=null;if(timer)clearTimeout(timer);options.retire();emit();};
 return {
  start(){stopped=false;challenge=options.nonce();options.post({type:'pm:projects-access-ready',nonce:challenge});},
  stop(){stopped=true;revoke();subscribers.clear();},
  subscribe(fn:()=>void){subscribers.add(fn);return()=>{subscribers.delete(fn);};},
  current(){return lease&&lease.expiresAt>options.now()?lease:null;},
  async receive(event:MessageEvent){
   if(stopped||event.origin!==SAI_ORIGIN||event.source!==options.parent||!event.data||typeof event.data!=='object')return;
   const m=event.data as Record<string,unknown>;
   if(m.type!=='sai:projects-access'||m.nonce!==challenge||m.workspace!==options.workspace)return;
   if(m.allowed!==true){revoke();return;}
   const expiry=typeof m.expiresAt==='string'?Date.parse(m.expiresAt):NaN;
   if(typeof m.principalId!=='string'||!m.principalId||typeof m.grant!=='string'||! /^[A-Za-z0-9_-]{32,256}$/.test(m.grant)
      ||!Number.isFinite(expiry)||expiry<=options.now()||expiry>options.now()+35000
      ||!Number.isSafeInteger(m.policyVersion)||Number(m.policyVersion)<0||!Number.isSafeInteger(m.memberVersion)||Number(m.memberVersion)<0){revoke();return;}
   if(lease&&(lease.principalId!==m.principalId||Number(m.policyVersion)<lease.policyVersion||Number(m.memberVersion)<lease.memberVersion)){revoke();return;}
   const own=++sequence;
   try{
    const response=await options.exchange(m.grant);
    if(stopped||own!==sequence||expiry<=options.now())return;
    if(typeof response.token!=='string'||!response.token||response.binding?.tenantId!==options.workspace||response.binding?.principalId!==m.principalId)throw Error('Mismatched constrained token');
    lease={token:response.token,grant:m.grant,workspace:options.workspace,principalId:m.principalId,expiresAt:expiry,policyVersion:Number(m.policyVersion),memberVersion:Number(m.memberVersion)};
    if(timer)clearTimeout(timer);timer=setTimeout(revoke,Math.max(1,expiry-options.now()));emit();
   }catch{if(own===sequence)revoke();}
  },
 };
}

let activeLease:EmbedLease|null=null;
// Once admitted as an embedded document, in-app query navigation cannot
// accidentally downgrade later requests to the independent PM session.
let embeddedDocument=false;
export function embedRequested(){
 if(typeof window==='undefined')return false;
 if(window.parent!==window&&new URLSearchParams(window.location.search).get('saiEmbed')==='1')embeddedDocument=true;
 return embeddedDocument;
}
export function setEmbedLease(value:EmbedLease|null){activeLease=value;if(typeof window!=='undefined')window.dispatchEvent(new Event('sai-embed-lease-updated'));}
export function getEmbedLease(){return activeLease&&activeLease.expiresAt>Date.now()?activeLease:null;}
export function embedHeaders():Record<string,string>{
 if(!embedRequested())return{};
 const current=getEmbedLease();if(!current)throw Error('SAI Projects access is unavailable');
 return{Authorization:`Bearer ${current.token}`,'X-SAI-Projects-Grant':current.grant};
}
export function embedMessageFields(){
 if(!embedRequested())return{};
 const current=getEmbedLease();if(!current)throw Error('SAI Projects access is unavailable');
 return{saiProjectsGrant:current.grant};
}
